import React, {useState} from "react";
import {sortableContainer, sortableElement, sortableHandle} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import Survey from './survey';
import { SURVEY_ANSWERS_TYPE, SURVEY_ANSWERS_TYPE_HAS_CONDITIONAL, SURVEY_QUESTION_CONDITION_TYPE } from '../../../utils/enums';

const SortableContainer = sortableContainer(({children}) => {
return <ul style={{paddingLeft: 0}}>{children}</ul>;
});

const DragHandle = sortableHandle(() => <div className="dragArea">
  <img className="sort-icon" src={require("../../../assets/images/sort.svg")} alt="sort surveys" />
</div>);

const addConditionalQuestion = (parentItem) => ({
    id: `${parentItem.id}-conditional-${Date.now()}`,
    number: null,
    title: '',
    answerType: 0,
    AIFilterKeywords: '',
    required: false,
    answerOptions: '',
    conditionalQuestions: [],
    surveyQuestionParentId: parentItem.id,
    SubQuestionParentConditionType: SURVEY_QUESTION_CONDITION_TYPE.Equals,
    subQuestionParentConditionValue: parentItem.answerType.toString() === SURVEY_ANSWERS_TYPE.Bool.toString() ? 'true' : ''
});

const normalizeConditionalQuestions = (conditionalQuestions, answerType) => {
    if (answerType.toString() === SURVEY_ANSWERS_TYPE.Bool.toString()) {
        return (conditionalQuestions || []).map((conditionalQuestion) => ({
            ...conditionalQuestion,
            SubQuestionParentConditionType: SURVEY_QUESTION_CONDITION_TYPE.Equals,
            subQuestionParentConditionValue: conditionalQuestion.subQuestionParentConditionValue === 'false' ? 'false' : 'true'
        }));
    }

    if (answerType.toString() === SURVEY_ANSWERS_TYPE.OptionalDecimal.toString()) {
        return (conditionalQuestions || []).map((conditionalQuestion) => ({
            ...conditionalQuestion,
            SubQuestionParentConditionType: conditionalQuestion.SubQuestionParentConditionType ?? SURVEY_QUESTION_CONDITION_TYPE.Equals,
            subQuestionParentConditionValue: conditionalQuestion.subQuestionParentConditionValue === 'true' || conditionalQuestion.subQuestionParentConditionValue === 'false' ? '' : (conditionalQuestion.subQuestionParentConditionValue ?? '')
        }));
    }

    return conditionalQuestions || [];
};

const updateSingleSurvey = (surveyItem, itemId, updater) => {
    if (surveyItem.id === itemId) {
        return updater(surveyItem);
    }

    return {
        ...surveyItem,
        conditionalQuestions: (surveyItem.conditionalQuestions || []).map((conditionalQuestion) => updateSingleSurvey(conditionalQuestion, itemId, updater))
    };
};

const updateSurveyTree = (surveyItems, itemId, updater) => {
    return surveyItems.map((surveyItem) => updateSingleSurvey(surveyItem, itemId, updater));
};

const removeSingleSurvey = (surveyItem, itemId) => {
    if (surveyItem.id === itemId) {
        return null;
    }

    return {
        ...surveyItem,
        conditionalQuestions: (surveyItem.conditionalQuestions || [])
            .map((conditionalQuestion) => removeSingleSurvey(conditionalQuestion, itemId))
            .filter(Boolean)
    };
};

const removeSurveyFromTree = (surveyItems, itemId) => {
    return surveyItems
        .map((surveyItem) => removeSingleSurvey(surveyItem, itemId))
        .filter(Boolean);
};

const SortableItem = sortableElement(({item, removeItem, handleChange, addConditionalSurvey }) => (
    <div className="card">
        <div className="flex">
            <DragHandle />
            <Survey
                item={item}
                removeItem={removeItem}
                handleChange={handleChange}
                addConditionalSurvey={addConditionalSurvey}
            />
        </div>
    </div>
));

const Surveys = ({items, setItems}) => {
    
    const [activeKey, setActiveKey] = useState(-1);

    const refreshNumbers = (surveyItems) => {
        return surveyItems.map((surveyItem, index) => ({
            ...surveyItem,
            number: index + 1
        }));
    };

    const removeItem = (itemId) => {
        let updatedItems = removeSurveyFromTree(items, itemId);
        setItems([...refreshNumbers(updatedItems)]);
    }
    
    const onSortEnd = ({oldIndex, newIndex}) => {
        let sortItems = arrayMove(items, oldIndex, newIndex);
        setItems(refreshNumbers(sortItems));
    };

    function handleOnChange({target}) {
        const { id, value } = target;
        const field = id.split('-')[0];
        const itemId = id.substring(field.length +1);

        const updatedItems = updateSurveyTree(items, itemId, (surveyItem) => {
            const updatedSurvey = {
                ...surveyItem,
                [field]: value
            };

            if(field === 'answerType' && SURVEY_ANSWERS_TYPE_HAS_CONDITIONAL[value] !== true) {
                updatedSurvey.conditionalQuestions = [];
            }

            if(field === 'answerType' && SURVEY_ANSWERS_TYPE_HAS_CONDITIONAL[value] === true) {
                updatedSurvey.conditionalQuestions = normalizeConditionalQuestions(updatedSurvey.conditionalQuestions, value);
            }

            return updatedSurvey;
        });

        setItems([...updatedItems]);
    }

    const addConditionalSurvey = (parentItemId) => {
        const updatedItems = updateSurveyTree(items, parentItemId, (surveyItem) => ({
            ...surveyItem,
            conditionalQuestions: [...(surveyItem.conditionalQuestions || []), addConditionalQuestion(surveyItem)]
        }));

        setItems([...updatedItems]);
    };

    return (
        <div>
            <SortableContainer onSortEnd={onSortEnd} pressDelay={0} useDragHandle>
                {items.map((value, index) => (
                    <SortableItem
                        activeKey={activeKey}
                        setActiveKey={setActiveKey}
                        key={`survey-item-${value.id}`}
                        index={index}
                        item={value}
                        handleChange={handleOnChange}
                        removeItem={removeItem}
                        addConditionalSurvey={addConditionalSurvey}
                    />
                ))}
            </SortableContainer>
        </div>
    )

}

export default Surveys;